import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Snowflake, Check, X, Trash, MagnifyingGlass, DownloadSimple,
  GridFour, ArrowLeft, Circle, CheckCircle, Warning, Image as ImageIcon
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const SHELF_COLORS = [
  { bg: 'bg-indigo-500/10', border: 'border-indigo-500/25', text: 'text-indigo-400', accent: '#818CF8', slotOcc: 'bg-indigo-500/30', slotBorder: 'border-indigo-500/50' },
  { bg: 'bg-cyan-500/10', border: 'border-cyan-500/25', text: 'text-cyan-400', accent: '#22D3EE', slotOcc: 'bg-cyan-500/30', slotBorder: 'border-cyan-500/50' },
  { bg: 'bg-emerald-500/10', border: 'border-emerald-500/25', text: 'text-emerald-400', accent: '#34D399', slotOcc: 'bg-emerald-500/30', slotBorder: 'border-emerald-500/50' },
  { bg: 'bg-amber-500/10', border: 'border-amber-500/25', text: 'text-amber-400', accent: '#FBBF24', slotOcc: 'bg-amber-500/30', slotBorder: 'border-amber-500/50' },
];

const SAMPLE_TYPES = ['Cell Line', 'Plasmid', 'Bacterial Stock', 'Virus Stock', 'Protein', 'Tissue', 'RNA', 'DNA', 'Reagent', 'Other'];

// ==================== 9×9 SLOT GRID ====================
const SlotGrid = ({ slots, onSlotClick, selectedSlot, color }) => (
  <div className="ln2-grid" data-testid="freezer-slot-grid">
    <div className="ln2-grid-header">
      <div className="ln2-corner" />
      {[1,2,3,4,5,6,7,8,9].map(c => <div key={c} className="ln2-col-label">{c}</div>)}
    </div>
    {[1,2,3,4,5,6,7,8,9].map(row => (
      <div key={row} className="ln2-grid-row">
        <div className="ln2-row-label">{String.fromCharCode(64 + row)}</div>
        {[1,2,3,4,5,6,7,8,9].map(col => {
          const slot = slots.find(s => s.row === row && s.col === col);
          if (!slot) return <div key={col} className="ln2-slot ln2-slot--free" />;
          const isSelected = selectedSlot === slot.slot;
          return (
            <button
              key={col}
              onClick={() => onSlotClick(slot)}
              className={`ln2-slot ${slot.occupied ? 'ln2-slot--occupied' : 'ln2-slot--free'} ${isSelected ? 'ln2-slot--selected' : ''}`}
              title={slot.occupied ? `${slot.sample_id} (${slot.sample_type})` : `Empty — ${String.fromCharCode(64 + row)}${col}`}
              data-testid={`fslot-${slot.slot}`}
            >
              {slot.occupied && <div className="ln2-vial" />}
            </button>
          );
        })}
      </div>
    ))}
  </div>
);

// ==================== SLOT EDIT PANEL ====================
const SlotPanel = ({ boxId, slot, onClose, onRefresh }) => {
  const [form, setForm] = useState({
    sample_id: slot.sample_id || '',
    sample_type: slot.sample_type || '',
    owner: slot.owner || '',
    date_stored: slot.date_stored || new Date().toISOString().split('T')[0],
    notes: slot.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const rowLabel = String.fromCharCode(64 + slot.row);

  const assign = async () => {
    if (!form.sample_id.trim()) { toast.error('Sample ID is required'); return; }
    setSaving(true);
    try {
      await api.updateFreezer80Slot(boxId, slot.slot, { action: 'assign', ...form });
      toast.success(`Stored at ${boxId} ${rowLabel}${slot.col}`);
      onRefresh(); onClose();
    } catch { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  const clear = async () => {
    if (!window.confirm(`Remove sample from ${boxId} ${rowLabel}${slot.col}?`)) return;
    setSaving(true);
    try {
      await api.updateFreezer80Slot(boxId, slot.slot, { action: 'clear' });
      toast.success('Slot cleared');
      onRefresh(); onClose();
    } catch { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="ln2-panel" data-testid="freezer-slot-panel">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-slate-100">{boxId} — {rowLabel}{slot.col}</h3>
        <button onClick={onClose} className="text-slate-500 hover:text-slate-300"><X size={16} /></button>
      </div>
      {slot.occupied && (
        <div className="p-3 rounded-lg bg-cyan-500/8 border border-cyan-500/20 mb-3">
          <p className="text-sm font-medium text-cyan-300">{slot.sample_id}</p>
          {slot.sample_type && <p className="text-xs text-slate-400 mt-0.5">{slot.sample_type}</p>}
          {slot.owner && <p className="text-xs text-slate-500 mt-0.5">Owner: {slot.owner}</p>}
          {slot.date_stored && <p className="text-xs text-slate-500">Stored: {slot.date_stored}</p>}
          {slot.notes && <p className="text-xs text-slate-500 mt-1 italic">{slot.notes}</p>}
        </div>
      )}
      <div className="space-y-2">
        <div>
          <Label className="text-xs text-slate-400">Sample ID *</Label>
          <Input value={form.sample_id} onChange={e => setForm({...form, sample_id: e.target.value})} placeholder="HEK293-GFP-P5" className="input-field w-full mt-1" data-testid="fslot-sample-id" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs text-slate-400">Type</Label>
            <select value={form.sample_type} onChange={e => setForm({...form, sample_type: e.target.value})} className="input-field w-full mt-1" data-testid="fslot-sample-type">
              <option value="">Select...</option>
              {SAMPLE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs text-slate-400">Owner</Label>
            <Input value={form.owner} onChange={e => setForm({...form, owner: e.target.value})} placeholder="Name" className="input-field w-full mt-1" data-testid="fslot-owner" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs text-slate-400">Date Stored</Label>
            <Input type="date" value={form.date_stored} onChange={e => setForm({...form, date_stored: e.target.value})} className="input-field w-full mt-1" />
          </div>
          <div>
            <Label className="text-xs text-slate-400">Notes</Label>
            <Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="P5, 1M/vial" className="input-field w-full mt-1" data-testid="fslot-notes" />
          </div>
        </div>
      </div>
      <div className="flex gap-2 mt-3">
        <Button className="btn-primary flex-1" onClick={assign} disabled={saving} data-testid="fslot-save-btn">
          {saving ? '...' : <><Check size={14} className="mr-1" /> {slot.occupied ? 'Update' : 'Store'}</>}
        </Button>
        {slot.occupied && (
          <Button variant="outline" onClick={clear} disabled={saving} className="text-red-400 border-red-500/30 hover:bg-red-500/10" data-testid="fslot-clear-btn">
            <Trash size={14} />
          </Button>
        )}
      </div>
    </div>
  );
};

// ==================== 4×4 BOX GRID IN A RACK ====================
const RackBoxGrid = ({ rack, shelfColor, onBoxClick, activeBoxId }) => (
  <div data-testid={`rack-${rack.rack}-boxes`}>
    <div className="grid gap-1" style={{ gridTemplateColumns: '20px repeat(4, 1fr)' }}>
      {/* Header row */}
      <div />
      {[1, 2, 3, 4].map(c => (
        <div key={c} className="text-center text-[10px] text-slate-600 font-semibold pb-0.5">C{c}</div>
      ))}
      {/* Box rows */}
      {[1, 2, 3, 4].map(br => (
        <React.Fragment key={br}>
          <div className="flex items-center justify-center text-[10px] text-slate-600 font-semibold">R{br}</div>
          {rack.boxes.filter(b => b.box_row === br).sort((a, b) => a.box_col - b.box_col).map(box => {
            const pct = box.total > 0 ? Math.round((box.occupied / box.total) * 100) : 0;
            const isActive = activeBoxId === box.box_id;
            return (
              <button
                key={box.box_id}
                onClick={() => onBoxClick(box.box_id)}
                className={`freezer-box-cell ${isActive ? 'freezer-box-cell--active' : ''}`}
                title={`${box.box_id}: ${box.occupied}/${box.total} slots occupied`}
                data-testid={`box-${box.box_id}`}
              >
                <span className="text-[10px] text-slate-500 font-mono">{box.box_id.split('-').slice(2).join('-')}</span>
                <div className="w-full h-1 rounded-full bg-slate-700 mt-0.5">
                  <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: shelfColor.accent }} />
                </div>
                <span className="text-[9px] text-slate-500">{box.occupied}/{box.total}</span>
              </button>
            );
          })}
        </React.Fragment>
      ))}
    </div>
  </div>
);

// ==================== SEARCH RESULTS ====================
const SearchResults = ({ results, onNavigate }) => (
  <div className="space-y-1" data-testid="search-results">
    {results.map((r, i) => (
      <button key={i} onClick={() => onNavigate(r.box_id)}
        className="w-full text-left p-3 rounded-lg bg-slate-800/40 border border-slate-700/20 hover:border-indigo-500/30 transition-all">
        <div className="flex items-center justify-between">
          <div>
            <span className="text-xs font-mono font-semibold text-indigo-400">{r.box_id}</span>
            <span className="text-xs text-slate-500 ml-2">Slot {String.fromCharCode(64 + r.slot_row)}{r.slot_col}</span>
          </div>
          <span className="text-xs text-slate-500">{r.sample_type}</span>
        </div>
        <p className="text-sm text-slate-200 mt-0.5">{r.sample_id}</p>
        {r.owner && <p className="text-xs text-slate-500">Owner: {r.owner}</p>}
      </button>
    ))}
    {results.length === 0 && <p className="text-sm text-slate-500 text-center py-4">No matching samples found</p>}
  </div>
);

// ==================== MAIN PAGE ====================
const Freezer80Page = () => {
  const [overview, setOverview] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [activeShelf, setActiveShelf] = useState(1);
  const [activeBoxId, setActiveBoxId] = useState(null);
  const [boxData, setBoxData] = useState(null);
  const [loadingBox, setLoadingBox] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);

  const loadOverview = useCallback(async () => {
    try {
      const data = await api.getFreezer80Overview();
      setOverview(data || []);
      setInitialized(data?.length > 0 && data[0].total > 0);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    (async () => { setLoading(true); await loadOverview(); setLoading(false); })();
  }, [loadOverview]);

  const initFreezer = async () => {
    setInitializing(true);
    try {
      await api.initFreezer80();
      toast.success('Freezer initialized: 256 boxes × 81 slots = 20,736 positions');
      await loadOverview();
    } catch { toast.error('Failed'); }
    finally { setInitializing(false); }
  };

  const openBox = useCallback(async (boxId) => {
    setLoadingBox(true);
    setSelectedSlot(null);
    setSearchResults(null);
    try {
      const data = await api.getFreezer80Box(boxId);
      setBoxData(data);
      setActiveBoxId(boxId);
    } catch { toast.error('Failed to load box'); }
    finally { setLoadingBox(false); }
  }, []);

  const refreshBox = useCallback(async () => {
    if (!activeBoxId) return;
    const data = await api.getFreezer80Box(activeBoxId);
    setBoxData(data);
    await loadOverview();
  }, [activeBoxId, loadOverview]);

  const handleSearch = async () => {
    if (!search.trim()) { setSearchResults(null); return; }
    setSearching(true);
    try {
      const results = await api.searchFreezer80(search.trim());
      setSearchResults(results);
    } catch { toast.error('Search failed'); }
    finally { setSearching(false); }
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('cl_access_token');
      const baseUrl = process.env.REACT_APP_BACKEND_URL;
      const resp = await fetch(`${baseUrl}/api/freezer80/export`, { headers: { 'Authorization': `Bearer ${token}` } });
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'freezer80_inventory.csv'; a.click();
      URL.revokeObjectURL(url);
      toast.success('CSV exported');
    } catch { toast.error('Export failed'); }
  };

  const currentShelf = overview.find(s => s.shelf === activeShelf);
  const totalOcc = overview.reduce((s, sh) => s + sh.occupied, 0);
  const totalSlots = overview.reduce((s, sh) => s + sh.total, 0);

  return (
    <div data-testid="freezer80-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="heading-display">-80°C Freezer Inventory</h1>
          <p className="text-slate-500 mt-1">4 shelves · 4 racks · 16 boxes/rack · 81 slots/box — 20,736 positions</p>
        </div>
        <div className="flex items-center gap-2">
          {initialized && (
            <Button variant="outline" className="btn-secondary" onClick={handleExport} data-testid="export-csv-btn">
              <DownloadSimple size={16} className="mr-2" /> Export CSV
            </Button>
          )}
          {!initialized && !loading && (
            <Button className="btn-primary" onClick={initFreezer} disabled={initializing} data-testid="init-freezer-btn">
              {initializing ? 'Initializing...' : <><Snowflake size={16} className="mr-2" /> Initialize Freezer</>}
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-32"><div className="spinner" /></div>
      ) : !initialized ? (
        <div className="text-center py-16">
          <Snowflake size={56} className="mx-auto text-slate-600 mb-3" />
          <p className="text-slate-400 text-lg">No freezer initialized</p>
          <p className="text-sm text-slate-500 mt-1">Click to set up 256 boxes with 81 slots each</p>
        </div>
      ) : (
        <>
          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            {overview.map((sh, i) => {
              const color = SHELF_COLORS[i];
              return (
                <button key={sh.shelf} onClick={() => { setActiveShelf(sh.shelf); setActiveBoxId(null); setBoxData(null); setSearchResults(null); }}
                  className={`card-flat p-4 text-left transition-all hover:scale-[1.02] cursor-pointer border ${activeShelf === sh.shelf ? color.border : 'border-transparent'}`}
                  data-testid={`shelf-stat-${sh.shelf}`}>
                  <p className={`text-2xl font-bold ${color.text}`}>{sh.occupied}<span className="text-sm text-slate-500 font-normal">/{sh.total}</span></p>
                  <p className="text-xs text-slate-500">Shelf {sh.shelf}</p>
                </button>
              );
            })}
          </div>

          {/* Search bar */}
          <div className="flex items-center gap-2 mb-5">
            <MagnifyingGlass size={16} className="text-slate-500" />
            <Input value={search} onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              placeholder="Search samples across all boxes..." className="input-field flex-1 text-sm" data-testid="freezer-search" />
            <Button size="sm" className="btn-primary" onClick={handleSearch} disabled={searching} data-testid="freezer-search-btn">
              {searching ? '...' : 'Search'}
            </Button>
            {searchResults && (
              <button onClick={() => setSearchResults(null)} className="text-slate-500 hover:text-slate-300"><X size={16} /></button>
            )}
          </div>

          {/* Search results */}
          {searchResults && (
            <div className="mb-6">
              <p className="text-xs text-slate-500 mb-2">{searchResults.length} results for "{search}"</p>
              <SearchResults results={searchResults} onNavigate={(boxId) => {
                const parts = boxId.split('-');
                setActiveShelf(parseInt(parts[0].replace('S', '')));
                openBox(boxId);
                setSearchResults(null);
              }} />
            </div>
          )}

          {/* Main content */}
          {!searchResults && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* LEFT: Shelf → Rack → Box grid */}
              <div className="lg:col-span-1 space-y-4">
                {/* Shelf tabs */}
                <div>
                  <Label className="text-xs text-slate-500 uppercase tracking-wider mb-2 block">Shelf</Label>
                  <div className="grid grid-cols-4 gap-2" data-testid="shelf-selector">
                    {[1,2,3,4].map(sh => {
                      const color = SHELF_COLORS[sh - 1];
                      return (
                        <button key={sh}
                          onClick={() => { setActiveShelf(sh); setActiveBoxId(null); setBoxData(null); setSelectedSlot(null); }}
                          className={`ln2-tower-btn ${activeShelf === sh ? 'ln2-tower-btn--active' : ''}`}
                          style={activeShelf === sh ? { borderColor: color.accent + '66', background: color.accent + '15' } : {}}
                          data-testid={`shelf-btn-${sh}`}>
                          <span className="text-sm font-bold">{sh}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Racks for current shelf */}
                {currentShelf?.racks.map(rack => {
                  const color = SHELF_COLORS[activeShelf - 1];
                  return (
                    <div key={rack.rack} className={`p-3 rounded-xl ${color.bg} border ${color.border}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className={`text-xs font-semibold ${color.text}`}>Rack {rack.rack}</span>
                        <span className="text-[10px] text-slate-500">{rack.occupied}/{rack.total} slots</span>
                      </div>
                      <RackBoxGrid rack={rack} shelfColor={color} onBoxClick={openBox} activeBoxId={activeBoxId} />
                    </div>
                  );
                })}

                {/* Legend */}
                <div className="flex items-center gap-4 p-3 rounded-lg bg-slate-800/20 border border-slate-700/15">
                  <span className="text-xs text-slate-500 font-medium">Shelves:</span>
                  {SHELF_COLORS.map((c, i) => (
                    <span key={i} className={`text-xs font-medium px-2 py-0.5 rounded ${c.bg} ${c.text} border ${c.border}`}>{i + 1}</span>
                  ))}
                </div>
              </div>

              {/* RIGHT: 9×9 slot grid + panel */}
              <div className="lg:col-span-2">
                {activeBoxId && boxData ? (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <button onClick={() => { setActiveBoxId(null); setBoxData(null); setSelectedSlot(null); }}
                          className="text-slate-500 hover:text-slate-300"><ArrowLeft size={18} /></button>
                        <h3 className="text-base font-semibold text-slate-100">Box {boxData.box_id}</h3>
                        <span className="text-xs text-slate-500">
                          {boxData.slots.filter(s => s.occupied).length} / 81 slots used
                        </span>
                      </div>
                    </div>

                    <div className="flex gap-4">
                      <SlotGrid
                        slots={boxData.slots}
                        onSlotClick={slot => setSelectedSlot(slot.slot === selectedSlot ? null : slot.slot)}
                        selectedSlot={selectedSlot}
                        color={SHELF_COLORS[activeShelf - 1]}
                      />
                      {selectedSlot && (
                        <div className="flex-shrink-0 w-72">
                          <SlotPanel
                            boxId={activeBoxId}
                            slot={boxData.slots.find(s => s.slot === selectedSlot)}
                            onClose={() => setSelectedSlot(null)}
                            onRefresh={refreshBox}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ) : loadingBox ? (
                  <div className="flex items-center justify-center h-64"><div className="spinner" /></div>
                ) : (
                  <div className="flex items-center justify-center h-64 rounded-xl bg-slate-800/20 border border-slate-700/15">
                    <div className="text-center">
                      <GridFour size={48} className="mx-auto text-slate-600 mb-2" />
                      <p className="text-sm text-slate-500">Select a box from Shelf {activeShelf} to view its 9×9 slot grid</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Freezer80Page;
